import { useCallback, useEffect, useRef, useState } from 'react';
import { AudioLines, MessageCircle, RotateCcw, Square, X } from 'lucide-react';
import { useFiloRemote } from '@/connection/FiloRemoteContext';
import { useChatAppController, type ChatPhase } from './useChatAppController';

export type ChatAppOverlayProps = {
  robotId: string;
  robotName: string;
  speaker: number;
  onClose: () => void;
};

const STATUS_LABELS: Record<ChatPhase, string> = {
  IDLE: 'じゅんびしてる',
  STARTING: 'じゅんびしてる',
  FACE_SEARCH: 'さがしてる',
  GREETING: 'はなしてる',
  LISTENING: 'きいてる',
  THINKING: 'かんがえてる',
  SPEAKING: 'はなしてる',
  FAREWELL: 'はなしてる',
  ENDED: 'おわったよ',
  ERROR: 'エラー',
};

export function ChatAppOverlay({
  robotId,
  robotName,
  speaker,
  onClose,
}: ChatAppOverlayProps) {
  const remote = useFiloRemote();
  const stream = remote.snapshot.sessions[robotId]?.remoteStream ?? null;
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [videoElement, setVideoElement] = useState<HTMLVideoElement | null>(null);
  const setVideoRef = useCallback((element: HTMLVideoElement | null) => {
    videoRef.current = element;
    setVideoElement(element);
  }, []);
  const controller = useChatAppController({
    robotId,
    speaker,
    stream,
    videoElement,
    remote,
  });

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    video.srcObject = stream;
    if (stream) void video.play().catch(() => undefined);
    return () => {
      if (video.srcObject === stream) video.srcObject = null;
    };
  }, [stream]);

  const ended = controller.phase === 'ENDED';
  const failed = controller.phase === 'ERROR';
  const listening = controller.phase === 'LISTENING';
  const audioDetected = listening && controller.audioLevels.some((level) => level >= 0.18);

  return (
    <div
      className="chat-app-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="chat-app-title"
    >
      <header className="chat-app-overlay__header">
        <div>
          <p className="eyebrow">FILO APP</p>
          <h2 id="chat-app-title">おしゃべり</h2>
          <p>{robotName}</p>
        </div>
        <div className="chat-app-overlay__header-actions">
          <span className={`chat-app-status chat-app-status--${controller.phase.toLowerCase()}`}>
            {STATUS_LABELS[controller.phase]}
          </span>
          {ended ? (
            <button type="button" className="chat-app-icon-button" onClick={onClose} aria-label="おしゃべり画面を閉じる">
              <X aria-hidden="true" size={20} />
            </button>
          ) : (
            <button
              type="button"
              className="chat-app-end-button"
              onClick={() => void controller.stopImmediately()}
            >
              <Square aria-hidden="true" size={15} />
              終了
            </button>
          )}
        </div>
      </header>

      <div className="chat-app-overlay__body">
        <section className="chat-app-video-panel" aria-label={`${robotName}のカメラ映像`}>
          <video
            ref={setVideoRef}
            autoPlay
            playsInline
            muted
          />
          {!stream && !failed && (
            <div className="chat-app-video-panel__placeholder">
              <span className="chat-app-video-panel__pulse" aria-hidden="true" />
              カメラをじゅんびしています
            </div>
          )}
          <div className="chat-app-video-panel__caption">
            <span className="chat-app-video-panel__indicator" aria-hidden="true" />
            {STATUS_LABELS[controller.phase]}
          </div>
        </section>

        <section className="chat-app-log" aria-label="おしゃべりの記録" aria-live="polite">
          <div className="chat-app-log__heading">
            <MessageCircle aria-hidden="true" size={18} />
            <h3>おしゃべり</h3>
          </div>
          {!failed && !ended && (
            <div className="chat-app-listening-monitor">
              <div className="chat-app-listening-monitor__status" aria-live="polite">
                <AudioLines aria-hidden="true" size={17} />
                <span>
                  {listening
                    ? audioDetected ? '音声を受信しています' : '音声を待っています'
                    : '聞き取りは待機中です'}
                </span>
              </div>
              <div
                className={`chat-app-waveform${listening ? ' is-listening' : ''}`}
                role="meter"
                aria-label={audioDetected ? 'ロボットのマイク音声を受信中' : 'ロボットのマイク音声を待機中'}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={Math.round((controller.audioLevels.at(-1) ?? 0) * 100)}
              >
                {controller.audioLevels.map((level, index) => (
                  <span
                    key={index}
                    aria-hidden="true"
                    style={{ height: `${Math.max(7, Math.round(level * 100))}%` }}
                  />
                ))}
              </div>
              <p className="chat-app-partial-transcript">
                <span>ききとったことば</span>
                {controller.partialTranscript || (
                  listening
                    ? '話すと、ここに途中の文字が表示されます'
                    : '聞き取りを始めるまでお待ちください'
                )}
              </p>
            </div>
          )}
          <div className="chat-app-log__entries">
            {controller.logs.length === 0 && !failed && !ended && (
              <p className="chat-app-log__empty">
                かおをみつけたら、おしゃべりをはじめます
              </p>
            )}
            {controller.logs.map((entry) => (
              <p
                key={entry.id}
                className={`chat-app-bubble chat-app-bubble--${entry.role}`}
              >
                <span>{entry.role === 'assistant' ? 'うぃーご' : 'あなた'}</span>
                {entry.text}
              </p>
            ))}
            {failed && (
              <div className="chat-app-error" role="alert">
                <p>{controller.errorMessage ?? 'おしゃべりを続けられませんでした'}</p>
                <div>
                  <button type="button" onClick={controller.retry}>
                    <RotateCcw aria-hidden="true" size={15} />
                    もう一度
                  </button>
                  <button type="button" onClick={onClose}>閉じる</button>
                </div>
              </div>
            )}
            {ended && (
              <div className="chat-app-ended">
                <p>おしゃべりを終了しました</p>
                <button type="button" onClick={onClose}>閉じる</button>
              </div>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}
